/**
 * QuickViewModal.tsx
 *
 * Read-only quick view of Master BOM lines applying to a specific combo.
 * Spec: docs/superpowers/plans/2026-09-22-bom-master-composer-spec.md
 */
import { X, Layers, Box } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type {
  BomMasterWoodLine,
  BomMasterHardwareLine,
  MasterComboRow,
} from '@/services/boms/masterTypes'

interface QuickViewModalProps {
  isOpen: boolean
  onClose: () => void
  combo: MasterComboRow | null
  woodLines: BomMasterWoodLine[]
  hardwareLines: BomMasterHardwareLine[]
}

export function QuickViewModal({
  isOpen,
  onClose,
  combo,
  woodLines,
  hardwareLines,
}: QuickViewModalProps) {
  if (!isOpen || !combo) return null

  // Filter lines matching this combo
  const matchingWood = woodLines.filter(
    (w) =>
      (w.applicable_colors.includes('ALL') ||
        w.applicable_colors.includes(combo.color_code)) &&
      (w.applicable_assembly.includes('ALL') ||
        w.applicable_assembly.includes(combo.assembly_type)),
  )

  const matchingHardware = hardwareLines.filter(
    (h) =>
      (h.applicable_colors.includes('ALL') ||
        h.applicable_colors.includes(combo.color_code)) &&
      (h.applicable_assembly.includes('ALL') ||
        h.applicable_assembly.includes(combo.assembly_type)),
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                {combo.combo_code}
              </span>
              <h3 className="text-lg font-bold text-slate-900">
                Xem nhanh cấu hình: {combo.color_name} · {combo.assembly_label}
              </h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Chế độ chỉ đọc — Áp dụng {matchingWood.length} chi tiết gỗ và{' '}
              {matchingHardware.length} ngũ kim
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Wood Lines Section */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <Layers className="h-4 w-4 text-indigo-600" />
              <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                Chi tiết gỗ ({matchingWood.length})
              </h4>
            </div>

            {matchingWood.length === 0 ? (
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-center text-xs text-slate-500 italic">
                Chưa có chi tiết gỗ nào áp dụng cho tổ hợp này.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase text-xs tracking-wider">
                    <tr>
                      <th className="py-2.5 px-3 w-12 text-center">STT</th>
                      <th className="py-2.5 px-3">Tên chi tiết</th>
                      <th className="py-2.5 px-3">Nhóm</th>
                      <th className="py-2.5 px-3">Loại gỗ</th>
                      <th className="py-2.5 px-3 text-right">Dày TT</th>
                      <th className="py-2.5 px-3 text-right">Rộng TT</th>
                      <th className="py-2.5 px-3 text-right">Dài TT</th>
                      <th className="py-2.5 px-3 text-right">Dày Thô</th>
                      <th className="py-2.5 px-3 text-right">Rộng Thô</th>
                      <th className="py-2.5 px-3 text-right">Dài Thô</th>
                      <th className="py-2.5 px-3 text-center">Mộng</th>
                      <th className="py-2.5 px-3 text-right font-bold">SL/SP</th>
                      <th className="py-2.5 px-3">Veneer</th>
                      <th className="py-2.5 px-3">Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {matchingWood.map((w, idx) => (
                      <tr key={w.id} className="hover:bg-slate-50/80">
                        <td className="py-2 px-3 text-center text-slate-400 font-mono">
                          {idx + 1}
                        </td>
                        <td className="py-2 px-3 font-medium text-slate-900">
                          {w.part_name}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {w.part_group || '—'}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {w.wood_type_raw || '—'}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-700 font-mono">
                          {w.dims_finished_t ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-700 font-mono">
                          {w.dims_finished_w ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-700 font-mono">
                          {w.dims_finished_l ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-500 font-mono">
                          {w.dims_rough_t ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-500 font-mono">
                          {w.dims_rough_w ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-500 font-mono">
                          {w.dims_rough_l ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-center text-slate-600">
                          {w.tenon || '—'}
                        </td>
                        <td className="py-2 px-3 text-right font-bold text-slate-900 font-mono">
                          {w.qty_per}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {w.veneer_spec || '—'}
                        </td>
                        <td className="py-2 px-3 text-slate-500 italic max-w-xs truncate">
                          {w.notes || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Hardware Lines Section */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <Box className="h-4 w-4 text-amber-600" />
              <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                Ngũ kim ({matchingHardware.length})
              </h4>
            </div>

            {matchingHardware.length === 0 ? (
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-center text-xs text-slate-500 italic">
                Chưa có ngũ kim nào áp dụng cho tổ hợp này.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase text-xs tracking-wider">
                    <tr>
                      <th className="py-2.5 px-3 w-12 text-center">STT</th>
                      <th className="py-2.5 px-3">Tên vật tư</th>
                      <th className="py-2.5 px-3">Quy cách</th>
                      <th className="py-2.5 px-3 text-right font-bold">SL/SP</th>
                      <th className="py-2.5 px-3">ĐVT</th>
                      <th className="py-2.5 px-3">Màu</th>
                      <th className="py-2.5 px-3">Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {matchingHardware.map((h, idx) => (
                      <tr key={h.id} className="hover:bg-slate-50/80">
                        <td className="py-2 px-3 text-center text-slate-400 font-mono">
                          {idx + 1}
                        </td>
                        <td className="py-2 px-3 font-medium text-slate-900">
                          {h.name_raw}
                        </td>
                        <td className="py-2 px-3 text-slate-600 font-mono">
                          {h.spec_raw || '—'}
                        </td>
                        <td className="py-2 px-3 text-right font-bold text-slate-900 font-mono">
                          {h.qty_per}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {h.uom_hint || 'Cái'}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {h.color_hint || '—'}
                        </td>
                        <td className="py-2 px-3 text-slate-500 italic max-w-xs truncate">
                          {h.notes || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="secondary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}
